/*
  Product info.
  Makes the add to cart form a fixed bar at the bottom on small screens once it scrolls out of view,
  syncs the quantity input with the form and keeps the printable gift card links on the selected variant.

  HTML Usage: see sections/main-product.liquid
*/

class ProductInfo extends HTMLElement {
  constructor() {
    super();
    this.fixedClass = 'is-fixed';
    this.hidingClass = 'hiding';
    this.fixedTimeout = null;

    this.productForm = this.closest('product-form-component');

    this.quickAddWrapper = this.querySelector('[data-product-form]');
    this.quickAddTrigger = this.querySelector('[data-product-form-trigger]');

    this.productFormQuantity = this.productForm?.querySelector('input[name="quantity"]');
    this.quantityInputElement = this.querySelector('quantity-input input[name="quantity"]');

    // Reduce actions (so the events can also be removed)
    this.reducer = {
      scroll: debounce(() => this.toggleFixedForm(), 10),
      quantity: () => this.handleQuantityInput(),
      variantChange: event => this.updateGiftCardPrintLinks(event.detail?.variant?.id),
    };

    this.quantityInputElement?.addEventListener('change', this.reducer.quantity);
    window.addEventListener('scroll', this.reducer.scroll);
    window.addEventListener('resize', this.reducer.scroll);
    document.addEventListener('variant-change', this.reducer.variantChange);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.reducer.scroll);
    window.removeEventListener('resize', this.reducer.scroll);
    document.removeEventListener('variant-change', this.reducer.variantChange);
    clearTimeout(this.fixedTimeout);
  }

  /**
   * toggleFixedForm
   * @description Fix the add to cart form to the bottom once its top scrolls out of view, on every screen width.
   * Not on screens lower than 480px, the fixed bar would cover too much.
   */
  toggleFixedForm() {
    if (!this.quickAddWrapper) return;

    const hasRoom = window.innerHeight > 480;
    const scrolledPast = this.quickAddWrapper.getBoundingClientRect().top <= 0;

    if (hasRoom && scrolledPast) {
      if (this.quickAddWrapper.classList.contains(this.fixedClass) || this.fixedTimeout) return;

      // Slide out first, then fix. The trigger is only visible once fixed, so measure it after that for the body padding.
      this.quickAddWrapper.classList.add(this.hidingClass);
      this.fixedTimeout = setTimeout(() => {
        this.quickAddWrapper.classList.add(this.fixedClass);
        this.quickAddWrapper.classList.remove(this.hidingClass);
        this.fixedTimeout = null;
        document.body.style.paddingBottom = `${this.quickAddTrigger?.offsetHeight || 0}px`;
      }, 1000);
      return;
    }

    clearTimeout(this.fixedTimeout);
    this.fixedTimeout = null;
    this.quickAddWrapper.classList.remove(this.fixedClass, this.hidingClass);
    // Close the opened fixed form, otherwise its open-state styles stay on the inline form
    document.body.classList.remove('pdp-form-is-open');
    document.body.style.paddingBottom = 0;
  }

  /**
   * updateGiftCardPrintLinks
   * @description Set the variant parameter of the printable gift card links to the selected variant.
   * @param {Number} variantId
   */
  updateGiftCardPrintLinks(variantId) {
    if (!variantId) return;
    this.querySelectorAll('a[href*="/pages/gift-card-print"]').forEach((link) => {
      const url = new URL(link.getAttribute('href'), window.location.origin);
      url.searchParams.set('variant', variantId);
      link.setAttribute('href', `${url.pathname}${url.search}`);
    });
  }

  /**
   * handleQuantityInput
   * @description Set the quantity from the quantity input web component to the product form web component.
   */
  handleQuantityInput() {
    if (!this.productFormQuantity || !this.quantityInputElement) return;
    this.productFormQuantity.value = this.quantityInputElement.value;
  }
}

if (!customElements.get('product-info')) {
  customElements.define('product-info', ProductInfo);
}
